import classNames from 'classnames';
import Link from 'next/link';
import { motion, useAnimationControls } from 'framer-motion';
import { ShoppingCart, User, Menu, X, Search as SearchIcon, Phone } from 'lucide-react';

import useCategories from 'hooks/api/useCategories';
import useScroll from 'hooks/common/useScroll';
import Search from 'components/core/Search';
import Image from 'components/core/Image';
import useAuth from 'hooks/api/useAuth';
import useUserUpdate from 'hooks/api/useUserUpdate';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faUnlockKeyhole, faArrowRightFromBracket, faFolderTree, faTrash,
} from '@fortawesome/free-solid-svg-icons';
import {
  useContext, useState, useCallback, useMemo, useEffect, useRef,
} from 'react';
import { CartContext } from 'contexts/cartContext';
import { AppContext } from 'contexts';
import { NoticeContext } from 'contexts/notification';
import { APP_URLS } from 'constants/index';

import { useTranslation } from 'next-i18next';
import { useRouter } from 'next/router';
import useLanguageCheck from 'hooks/common/useLanguageCheck';
import sortCategories from 'utils/helpers/sortCategories';

import styles from './index.module.scss';
import DropdownMenu from './components/DropdownMenu';
import MobNav from './components/MobNav';
import AccountDeleteModal from './components/AccountDeleteModal';

const Header = () => {
  const { scroll } = useScroll({ type: 'header' });
  const [search, setSearch] = useState<boolean>(false);
  const { totalAmount = 0 } = useContext(CartContext);
  const { user, upsertUser, categories, updateCategories } = useContext(AppContext);
  const { data = [] } = useCategories({ enabled: !categories.length });
  const { authLogout } = useAuth();
  const { message } = useContext(NoticeContext);
  const [burger, setBurger] = useState<boolean>(false);
  const [AccountDeleteModalVisible, setAccountDeleteModalVisible] = useState(false);
  const { t } = useTranslation('header');
  const { t: tFooter } = useTranslation('footer');
  const router = useRouter();
  const { locale } = router;

  useEffect(() => {
    if (data.length && !categories.length) updateCategories(sortCategories(data));
  }, [categories.length, data, updateCategories]);

  // Bounce the header cart icon whenever the cart count grows (item added).
  const cartControls = useAnimationControls();
  const prevAmountRef = useRef(totalAmount);
  useEffect(() => {
    if (totalAmount > prevAmountRef.current) {
      cartControls.start({
        rotate: [0, -18, 14, -10, 6, 0],
        scale: [1, 1.3, 0.85, 1.15, 1],
        transition: { duration: 0.5, ease: 'easeInOut' },
      });
    }
    prevAmountRef.current = totalAmount;
  }, [totalAmount, cartControls]);

  const { changeLang, changeLangLoading } = useLanguageCheck();

  const handleLogout = useCallback(async () => {
    await authLogout().then(() => upsertUser()).catch((e) => message(e?.message, 'error'));
  }, [authLogout, message, upsertUser]);

  const actions = useMemo(() => {
    const data = [];
    if (user) {
      data.push(
        { href: '/recovery', icon: faUnlockKeyhole, title: t('recoveryPassword') },
        { href: '/orders', icon: faFolderTree, title: t('orders') },
        { icon: faTrash, title: t('deleteAccount'), onClick: () => setAccountDeleteModalVisible(true) },
        { icon: faArrowRightFromBracket, title: t('signOut'), onClick: handleLogout },
      );
    } else data.push({ icon: faArrowRightFromBracket, title: t('login'), href: '/login' });
    return data;
  }, [user, handleLogout, t]);

  const categoryActions = useMemo(
    () => categories.map((i) => ({ href: `/${i.name}`, title: i.name, image: i.image })),
    [categories],
  );

  const logo = (
    <Link href='/' className={styles.logoLink}>
      <motion.div
        className={styles.logoIcon}
        whileHover={{ rotate: [-0, -10, 10, -10, 0], scale: 1.05 }}
        transition={{ duration: 0.5 }}
      >
        <Image src='/logo.png?v=3' alt='Logo' width={32} height={32} priority />
      </motion.div>
      <Image src='/logo2.png' alt='Бібікалка' width={120} height={32} priority />
    </Link>
  );

  // Checkout: logo + support phone only, so nothing pulls the customer away from the order.
  if (router.pathname === APP_URLS.CHECKOUT) {
    const phone = tFooter('phone');
    return (
      <header className={classNames(styles.header, { [styles.headerColor]: scroll, [styles.headerNoColor]: !scroll })}>
        <div className={styles.container}>
          {logo}
          <a href={`tel:${phone.replace(/\s/g, '')}`} className={styles.supportPhone}>
            <Phone size={18} />
            <span className={styles.supportPhoneLabel}>{tFooter('support')}</span>
            <span>{phone}</span>
          </a>
        </div>
      </header>
    );
  }

  return (
    <header className={classNames(styles.header, { [styles.headerColor]: scroll, [styles.headerNoColor]: !scroll })}>
      <div className={styles.container}>

        {/* ── Logo ── */}
        {logo}

        {/* ── Search ── */}
        <div className={classNames(styles.searchCenter, { [styles.searchCenterOpen]: search })}>
          <Search search setSearch={setSearch} />
        </div>

        {/* ── Right actions ── */}
        <div className={styles.rightContainer}>

          {/* Language switcher */}
          <div className={styles.langSwitcher}>
            {(['ua', 'ru'] as const).map((lang, i) => (
              <>
                {i > 0 && <span className={styles.langDivider}>/</span>}
                <button
                  key={lang}
                  type='button'
                  className={classNames(styles.langBtn, { [styles.langBtnActive]: locale === lang })}
                  onClick={() => changeLang(lang)}
                  disabled={changeLangLoading}
                >
                  {lang.toUpperCase()}
                </button>
              </>
            ))}
          </div>

          {/* Categories dropdown */}
          {/*<div className={styles.category}>*/}
          {/*  {t('categories')}*/}
          {/*  <div className={styles.categoryWrap}>*/}
          {/*    <DropdownMenu open actions={categoryActions} />*/}
          {/*  </div>*/}
          {/*</div>*/}

          {/* User */}
          {/*{user*/}
          {/*  ? (*/}
          {/*    <div className={styles.user}>*/}
          {/*      <User size={20} />*/}
          {/*      <div className={styles.modal}>*/}
          {/*        <DropdownMenu open actions={actions} user={user} />*/}
          {/*      </div>*/}
          {/*    </div>*/}
          {/*  )*/}
          {/*  : (*/}
          {/*    <Link href={APP_URLS.LOGIN}>*/}
          {/*      <div className={styles.user}>*/}
          {/*        <User size={20} />*/}
          {/*        <span className={styles.login}>{t('login')}</span>*/}
          {/*      </div>*/}
          {/*    </Link>*/}
          {/*  )}*/}

          {/* Cart */}
          <Link href={APP_URLS.CHECKOUT} aria-label={t('cart', 'Кошик')}>
            <motion.div
              className={styles.iconBtn}
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
            >
              <motion.span animate={cartControls} style={{ display: 'inline-flex' }}>
                <ShoppingCart size={22} />
              </motion.span>
              {totalAmount > 0 && (
                <motion.span
                  className={styles.cartBadge}
                  initial={{ scale: 0 }}
                  animate={{ scale: 1 }}
                  transition={{ type: 'spring', stiffness: 500 }}
                >
                  {totalAmount}
                </motion.span>
              )}
            </motion.div>
          </Link>

          {/* Search icon (mobile only) */}
          <div className={styles.mobileSearchBtn} role='presentation' onClick={() => setSearch(true)}>
            <SearchIcon size={22} />
          </div>

          {/* Burger (mobile) */}
          {/*<div className={styles.burgerMenu} role='presentation' onClick={() => setBurger((s) => !s)}>*/}
          {/*  {burger ? <X size={22} /> : <Menu size={22} />}*/}
          {/*</div>*/}
        </div>
      </div>

      <MobNav
        handleLogout={handleLogout}
        categoryActions={categoryActions}
        actions={actions}
        open={burger}
        onClose={() => setBurger((s) => !s)}
      />
      <AccountDeleteModal visible={AccountDeleteModalVisible} handleClose={() => setAccountDeleteModalVisible(false)} />

    </header>
  );
};

export default Header;
